import { createFileRoute, useRouter, type ErrorComponentProps } from '@tanstack/react-router'
import { useId, useRef } from 'react'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import TabBar, { TabPanel, type TabBarOption } from '@/components/ui/TabBar'
import TitleRow from '@/components/ui/TitleRow'
import {
  useSuspenseFundStockCount,
  useSuspenseFundSummary,
} from '@/features/mutual-funds/api/queries'
import { defaultTrendFunds } from '@/features/mutual-funds/lib/defaults'
import {
  ANALYTICS_TABS,
  analyticsSearchSchema,
  analyticsTabSchema,
  loadAnalytics,
  type AnalyticsTab,
} from '@/screens/mutual-funds/AnalyticsScreen.loader'
import {
  AnalyticsStatsSkeleton,
  AnalyticsTabsSkeleton,
  StockCountTabSkeleton,
} from '@/screens/mutual-funds/components/analytics/AnalyticsSkeletons'
import AnalyticsStatStrip from '@/screens/mutual-funds/components/analytics/AnalyticsStatStrip'
import FundSectorsDialog from '@/screens/mutual-funds/components/analytics/FundSectorsDialog'
import PopularTab from '@/screens/mutual-funds/components/analytics/PopularTab'
import SectorsTab from '@/screens/mutual-funds/components/analytics/SectorsTab'
import StockCountTab from '@/screens/mutual-funds/components/analytics/StockCountTab'
import TrendsTab from '@/screens/mutual-funds/components/analytics/TrendsTab'

const TITLE = 'Mutual Funds'

/** Known without data, so the pending and failed pages draw the real one. */
const HEADER = <TitleRow title={TITLE} />

/**
 * The loaded page's own blocks, in order: the stat strip, then the tab row and the default tab
 * (Fund Stock Count) 16 apart — whichever tab the URL opens, since the loader awaits only what
 * that one needs and every other tab draws its own skeleton once the page is up.
 */
function AnalyticsPending() {
  return (
    <ScreenLayout pending pendingLabel="Loading mutual funds…" header={HEADER} pageTitle={TITLE}>
      <AnalyticsStatsSkeleton />
      <div className="flex flex-col gap-4">
        <AnalyticsTabsSkeleton />
        <StockCountTabSkeleton />
      </div>
    </ScreenLayout>
  )
}

function AnalyticsError(props: ErrorComponentProps) {
  return <RouteErrorPage {...props} header={HEADER} title="Mutual funds did not load" />
}

export const Route = createFileRoute('/_app/mutual-funds')({
  validateSearch: analyticsSearchSchema,
  loader: loadAnalytics,
  pendingComponent: AnalyticsPending,
  errorComponent: AnalyticsError,
  component: AnalyticsScreen,
})

const TAB_LABELS: Record<AnalyticsTab, string> = {
  'stock-count': 'Fund Stock Count',
  trends: 'Monthly Trends',
  popular: 'Most Popular',
  sectors: 'Sector Diversity',
}

const TAB_OPTIONS: readonly TabBarOption[] = ANALYTICS_TABS.map((tab) => ({
  value: tab,
  label: TAB_LABELS[tab],
}))

/**
 * Mutual funds · Analytics — the section's landing page: three headline
 * figures over the latest filings, then four views of them.
 *
 * The tiles and the first tab are the loader's two awaited reads, read here
 * with suspense, so neither ever blanks: a tab click is a search-only change
 * that re-runs no loader, and the other three tabs each read for themselves
 * inside their own panel, skeleton and failure included.
 *
 * Every view lives in the URL — the tab, the trends selection (`funds`,
 * written only once the member changes it; absent means the top three by
 * stock count) and the open sector dialog (`fund`).
 */
function AnalyticsScreen() {
  const idBase = useId()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const router = useRouter()
  const { data: summary } = useSuspenseFundSummary(null)
  const { data: stockCount } = useSuspenseFundStockCount(null)

  // A hand-edited `?funds=` can name a symbol the stock-count list has never
  // heard of (a typo, a delisted fund). The schema can't catch that — it has
  // no data to check against — so it is filtered here instead of rendered as
  // a chip nothing can load. Falls back to the default three only when NONE
  // of the named symbols are real, the same "nothing usable" rule the schema
  // itself uses when `funds` is absent. An empty `funds` is the member having
  // removed every chip, and stays empty.
  const knownFundSymbols = new Set(stockCount.map((fund) => fund.symbol))
  const requestedFunds = search.funds?.filter((symbol) => knownFundSymbols.has(symbol))
  const trendFunds =
    search.funds?.length === 0
      ? []
      : requestedFunds !== undefined && requestedFunds.length > 0
        ? requestedFunds
        : defaultTrendFunds(stockCount)

  // Opening the dialog pushes a history entry, so Back closes it. Closing it
  // from inside should then be that same Back — otherwise the entry stays
  // behind and the next Back press seems to do nothing. Only when THIS page
  // pushed the entry, though: a `?fund=` link opened cold has no page of
  // ours behind it to go back to, so that close replaces the URL instead.
  const openedFromPage = useRef(false)

  const openSectors = (symbol: string) => {
    openedFromPage.current = true
    void navigate({ search: (prev) => ({ ...prev, fund: symbol }) })
  }

  // Also runs on the dialog's own `close` event after the URL has already
  // dropped `fund` (Back, or the close below) — hence the guard.
  const closeSectors = () => {
    if (search.fund === undefined) return
    if (openedFromPage.current) {
      openedFromPage.current = false
      router.history.back()
      return
    }
    void navigate({ search: (prev) => ({ ...prev, fund: undefined }), replace: true })
  }

  return (
    <ScreenLayout header={HEADER} pageTitle={TITLE}>
      <AnalyticsStatStrip summary={summary} />

      {/* The tab row and its panel are one block, 16 apart (the pages revamp's one gap from a tab
          row to its first block). */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2 @md:flex-row @md:items-center @md:justify-between @md:gap-3">
          {/* Movers' treatment: four tabs run wider than a phone, so the row scrolls in its own
              track, bleeding through the shell gutter so the clipped tab at the edge is the
              affordance. `@sm:`, not `sm:` — this sits in ScreenLayout's main-column
              `@container`, and a viewport breakpoint cannot see behind the sidebar and the
              rail. */}
          <div className="scrollbar-slim -mx-shell-x min-w-0 overflow-x-auto overscroll-x-contain @sm:mx-0 @sm:overflow-x-visible">
            <TabBar
              stripClassName="px-shell-x @sm:px-0"
              options={TAB_OPTIONS}
              value={search.tab}
              ariaLabel="Mutual fund analytics"
              idBase={idBase}
              onChange={(value) => {
                const tab = analyticsTabSchema.parse(value)
                void navigate({ search: (prev) => ({ ...prev, tab }) })
              }}
            />
          </div>
          {/* The month the filings are for — every figure above is only true of it. Under the tabs below
              `@md`, beside them from there, so a phone-width column still says which month. */}
          {summary.period === null ? null : (
            <span className="text-body-xs text-muted-foreground-subtle @md:shrink-0">
              As of {summary.period}
            </span>
          )}
        </div>

        <TabPanel idBase={idBase} value={search.tab} className="flex flex-col gap-6">
          {search.tab === 'stock-count' ? (
            <StockCountTab funds={stockCount} period={summary.period} />
          ) : search.tab === 'trends' ? (
            <TrendsTab
              funds={stockCount}
              selected={trendFunds}
              onSelectedChange={(next) => {
                // An array, as Compare writes `codes`: every other navigation
                // here spreads `prev`, whose `funds` the schema has already
                // parsed into an array, so a comma string written here would
                // turn into the router's JSON form on the next tab click anyway.
                // `?funds=A,B` typed by hand still parses.
                //
                // `replace: true` as Compare does too: adding and removing funds
                // is one continuous edit to the same selection, not a sequence of
                // pages Back should step through one chip at a time.
                void navigate({ search: (prev) => ({ ...prev, funds: next }), replace: true })
              }}
            />
          ) : search.tab === 'popular' ? (
            <PopularTab />
          ) : (
            <SectorsTab onOpen={openSectors} />
          )}
        </TabPanel>
      </div>

      <FundSectorsDialog fund={search.fund} onClose={closeSectors} />
    </ScreenLayout>
  )
}
